import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	tableFeatures,
} from '@tanstack/react-table';
import { Edit } from 'lucide-react';

import type { OrganizationListItemType } from '@/common/organization';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import type { SortFieldMap } from '@/components/data-table/sort-param';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { t } from '@/i18n';
import { formatDateTime } from '@/lib/utils';

export interface OrganizationTableMeta {
	isUpdating: boolean;
	onToggleStatus: (organization: OrganizationListItemType, enabled: boolean) => void;
	onEdit: (organization: OrganizationListItemType) => void;
}

// Sorting is server-side (`manualSorting`), so no sorted row model or sort fns are registered.
export const organizationTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<OrganizationTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

// Column id → backend `sort` field. Must match ORGANIZATION_SORT_FIELDS in the backend DTO.
export const ORGANIZATION_SORT_FIELDS: SortFieldMap = {
	name: 'name',
	users: 'organizationUsers._count',
	createdAt: 'createdAt',
	status: 'enabled',
};

const columnHelper = createColumnHelper<typeof organizationTableFeatures, OrganizationListItemType>();

export const organizationColumns = columnHelper.columns([
	columnHelper.accessor('name', {
		id: 'name',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('admin.organizations.table.name')} />,
		cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
	}),
	columnHelper.accessor('usersCount', {
		id: 'users',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.users')} />,
		cell: ({ getValue }) => getValue() || 0,
		meta: { className: 'text-muted-foreground' },
	}),
	columnHelper.accessor('createdAt', {
		id: 'createdAt',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('admin.organizations.table.createdDate')} />,
		cell: ({ getValue }) => formatDateTime(getValue() || ''),
		meta: { className: 'text-muted-foreground' },
	}),
	columnHelper.accessor('enabled', {
		id: 'status',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.status')} />,
		cell: ({ row, table }) => (
			<div className="flex items-center gap-2">
				<Switch
					checked={row.original.enabled}
					onCheckedChange={(checked) => table.options.meta?.onToggleStatus(row.original, checked)}
					disabled={table.options.meta?.isUpdating}
					className="data-[state=checked]:bg-green-600"
				/>
				<span className="text-sm text-muted-foreground">{row.original.enabled ? t('common.states.active') : t('common.states.inactive')}</span>
			</div>
		),
		meta: { className: 'w-50' },
	}),
	columnHelper.display({
		id: 'actions',
		header: () => t('common.labels.actions'),
		cell: ({ row, table }) => (
			<div className="flex items-center justify-end gap-2">
				<Button
					className="hover:bg-blue-50 hover:text-primary"
					variant="ghost"
					size="sm"
					onClick={() => table.options.meta?.onEdit(row.original)}
				>
					<Edit />
				</Button>
			</div>
		),
		meta: { className: 'text-right', headerClassName: 'text-right' },
	}),
]);
